/*
 * Copyright 2026 Sony Corporation
 */
import { Box, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import { useAuth } from '../contexts/auth-context';
import { thumbnailCache } from '../services/thumbnail-cache';

import { Screen } from './DeviceScreen';
import { AppIcon } from './icons';

const HEIGHT = 150;

/**
 * The app's screen as its runtime last showed it, small; a placeholder while there is none. The picture is the
 * markup of the newest check of the page, shown like the project's own screen panel: sandboxed, no scripts.
 */
export const ProjectThumbnail = ({ projectId, title }: { projectId: string; title: string }) => {
  const { api } = useAuth();
  // What was shown last time comes first; the check below only replaces it when there is a newer screen.
  const [html, setHtml] = useState<string | null | undefined>(() => thumbnailCache.get(projectId)?.html);

  useEffect(() => {
    let live = true;
    const cached = thumbnailCache.get(projectId);
    setHtml(cached?.html);
    const load = async () => {
      try {
        const summaries = await api.listVerifications(projectId);
        const newest = summaries.find((v) => v.snapshot_labels.length > 0);
        if (!newest) {
          thumbnailCache.set(projectId, { verificationId: null, html: null });
          return null;
        }
        // The same check as last time: its markup has not changed, so it is not fetched again.
        if (cached && cached.verificationId === newest.verification_id) {
          return cached.html;
        }
        const verification = await api.getVerification(projectId, newest.verification_id);
        const found = verification.snapshots.filter((s) => s.html).at(-1)?.html ?? null;
        thumbnailCache.set(projectId, { verificationId: newest.verification_id, html: found });
        return found;
      } catch {
        // Offline or refused: keep what is shown.
        return cached ? cached.html : null;
      }
    };
    void load().then((found) => live && setHtml(found));
    return () => {
      live = false;
    };
  }, [api, projectId]);

  return (
    <Box sx={{ height: HEIGHT, overflow: 'hidden', bgcolor: 'action.hover', pointerEvents: 'none' }}>
      {html ? (
        <Screen html={html} title={`Screen of ${title}`} />
      ) : (
        <Stack sx={{ height: '100%', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
          <AppIcon sx={{ fontSize: 40 }} />
          <Typography variant="caption">{html === undefined ? 'Loading…' : 'No screen yet'}</Typography>
        </Stack>
      )}
    </Box>
  );
};
